import { UserSubjectHistory } from '../models/user-subject-history';
import { Subject } from '../models/subject';
import { MessageService } from './messages/message.service';
import { EventService } from './event.service';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserService } from './user.service';
import { Injectable, OnDestroy } from '@angular/core';
import { DataService } from './data.service';
import { Router } from '@angular/router';
import { catchError, concatMap, debounceTime, distinctUntilChanged, map, retry, take, tap } from 'rxjs/operators';
import { User } from '../models/user';
import { SubjectTypeService } from './subject-type.service';
import { SubjectType } from '../models/subject-type';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

// tslint:disable-next-line:max-classes-per-file
@Injectable({ providedIn: 'root' })
export class UserSubjectHistoryService extends DataService<UserSubjectHistory> implements OnDestroy {

  // notify a change in the collection of data to be displayed, returns the up to date collection for display
  onChanged: BehaviorSubject<any> = new BehaviorSubject([]);

  onViewSubject$: Subscription;
  onUpdateSubject$: Subscription;
  onDeleteSubject$: Subscription;

  currentType: string;
  resourceUrl = '/api/users';

  constructor(public http: HttpClient,
    private eventService: EventService,
    private userService: UserService,
    private subjectTypeService: SubjectTypeService,
    public router: Router,
    public messageService: MessageService) {
    super(UserSubjectHistoryService, 'usersubjecthistory', router);

    // enable caching
    this.cacheEnabled = true;

    this.onViewSubject$ = this.eventService.onViewSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe((subject: Subject) => {
      if (subject && subject.id) {
        const s = subject;
        this.save(s).subscribe();
      }
    });

    this.onUpdateSubject$ = this.eventService.onUpdateSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe((subject: Subject) => {
      if (subject && subject.id) {
        // update the current subject
        this.updateCacheWithSubject(subject);

        // update directly linked subjects, just in case
        if (subject.subjectLinks) {
          subject.subjectLinks.forEach((i) => {
            this.updateCacheWithSubject(i.linkedSubject);
            if (i.subject && (i.subjectId !== subject.id)) {
              this.updateCacheWithSubject(i.subject);
            }
          });
        }
      }
    });

    this.onDeleteSubject$ = this.eventService.onDeleteSubject.subscribe((subject) => {
      if (subject && this.cache) {
        // remove the subject from the user subject history cache
        this.cache = this.cache.filter((i) => i.subjectId !== subject.id);
      }
    });
  }

  ngOnDestroy() {
    this.onViewSubject$.unsubscribe();
    this.onUpdateSubject$.unsubscribe();
    this.onDeleteSubject$.unsubscribe();
  }

  updateCacheWithSubject(subject: Subject) {
    if (subject && this.cache) {
      const userSubjectHistory = this.cache.find((i) => i.subjectId === subject.id);
      if (userSubjectHistory) {
        userSubjectHistory.subject = subject;
        userSubjectHistory.primaryLink = new Subject(subject).primaryLink;
      }
    }
  }

  getCurrentType(): string {
    return this.currentType || this.subjectTypeService.getName(1) || 'Case';
  }

  forceRefresh() {
    this.clearCache();
    this.getAll(this.getCurrentType()); // .pipe(take(1)).subscribe();
  }

  // subscribes to getAllData and passes the result to subscribers via the onChanged subscription
  getAll(type: string) {
    this.currentType = type;
    this.getAllData(type.toLowerCase()).pipe(take(1)).subscribe((data) => {
      this.onChanged.next(data);
    }, (error) => { this.onChanged.next([]); });
  }

  // cold observable to return all data from cache or datastore
  getAllData(type: string): Observable<UserSubjectHistory[]> {

    // check for cached data...
    // we could use this.cache but this.getCache includes additional logging which helps with debug
    const cachedData = this.getCache();
    if (cachedData.length > 0) {
      // we have cached data - job done, filter and return it as an observable
      const filteredData = cachedData.filter((x) => (!x.subject.archived) && (x.subjectType.name.toLowerCase() === type.toLowerCase()));
      const sortedData = filteredData.sort((a, b) => b.id - a.id);
      return of(sortedData);
    }

    // no data in the cache, build the url we need to make the http get request
    const currentUserUid = this.userService.getCurrentUserUid();
    const url = this.resourceUrl + `/${currentUserUid}/subjects/recent`;

    // build the observable and assign to a local variable so we can chain it later in this function
    // Note: we are using map to manipulate data and the http get will only be called when we subscribe to the chain of observables we will eventually return
    const http$ = this.http.get<UserSubjectHistory[]>(url).pipe(
      retry({count:3, delay: 3000}),
      map((data) => {
        this.debug('Data received from API:', url, data);
        data = data || [];
        data.map((item) => { item = new UserSubjectHistory(item); });

        // now we have data returned we need to collect together the observables we will execute later...
        const hydrate$ = [];

        // ...starting with an observable which will return the data we are, eventually, going to return
        hydrate$.push(of(data));

        // the 'hydrate' function returns on observable for each item in the data array
        data.map((item) => { hydrate$.push(this.hydrate(item)); });

        // return the collection of observables ready for our chain
        return hydrate$;
      }),
      catchError(this.handleError<UserSubjectHistory>(`Get UserSubjectHistory`, true)),
    );

    // now we have built our observable which returns a collection of observables we chain everything together...
    // concatMap takes the response from our http$ observable (outer observable created above) and passes it to our inner observable - forkJoin
    // the forkJoin takes the array of observables from the outer observable and executes them, returning the last emitted value from each as an array.
    // Because we added of(data) observable at the beginning of our array of observables then
    // the data we want to stash and return is in the first element of the array returned by forkJoin
    // Note: Again we are not actually subscribing to anything here so nothing will actually happen until we subscribe to the getAllData method - ie getAllData is a cold observable
    return http$.pipe(concatMap((response: any[]) =>
      forkJoin(response).pipe(take(1), map((data) => {
        // stash the data we are interested in in local cache if caching enabled...
        this.cache = [...(data[0] as any)];
        // apply filter and return our data
        const filteredData = (data[0] as any).filter((x) => (!x.subject.archived) && (x.subjectType.name.toLowerCase() === type.toLowerCase()));
        const sortedData = filteredData.sort((a, b) => b.id - a.id);
        return sortedData;
      }),
        catchError(this.handleError<UserSubjectHistory>(`Get UserSubjectHistory - hydration`, false)),
      )));
  }

  save(entity: Subject, successMessage?: string): Observable<any> {
    const currentUserUid = this.userService.getCurrentUserUid();
    const url = this.resourceUrl + `/${currentUserUid}/subjects`;
    const subject = new Subject({
      id: entity.id,
      subjectTypeId: entity.subjectTypeId,
    });
    return this.http
      .post<UserSubjectHistory>(url, subject, httpOptions).pipe(
        tap((savedEntity: UserSubjectHistory) => {
          this.log(`Added ${this.CacheName} w/ id=${savedEntity.id}`);
          // hydrate item
          this.hydrate(new UserSubjectHistory(savedEntity)).pipe(take(1)).subscribe((hydratedEntity) => {
            hydratedEntity.subject = entity;
            hydratedEntity.primaryLink = new Subject(entity).primaryLink;
            this.updateAndNotifyItemChanged(hydratedEntity, successMessage);
            return hydratedEntity;
          });
        }),
        catchError(this.handleError<UserSubjectHistory>(`Add ${this.CacheName}`, true)));
  }

  updateAndNotifyItemChanged(entity: UserSubjectHistory, successMessage?: string) {
    // cache changes...
    this.cache = this.cache || [];
    this.cache = this.cache.filter((x) => x.subjectId !== entity.subjectId);
    this.cache.push(entity);

    const currentVal = this.onChanged.getValue();
    let val = (currentVal && (currentVal.length > 0)) ? currentVal : [];
    if (((val.length > 0) && (val[0].subjectType.name === entity.subjectType.name))
      || (((val.length === 0) && this.currentType) && entity.subjectType.name.toLowerCase() === this.currentType.toLowerCase())) {
      // update the visible (and filtered) list
      val = val.filter((x) => x.subjectId !== entity.subjectId);
      val.push(entity);
      // make sure that any subscribers are notified about the change
      this.onChanged.next(val);
    }
  }

  hydrate(item: UserSubjectHistory): Observable<UserSubjectHistory> {
    const hydrate$ = [];

    // user
    if ((!item.user) || (item.user.id !== item.userId)) {
      hydrate$.push(this.userService.getById(item.userId).pipe(take(1), map((user) => {
        item.user = new User(user);
        return item;
      })));
    }

    // subjectType
    if ((!item.subjectType) || (item.subjectType.id !== item.subjectTypeId)) {
      hydrate$.push(this.subjectTypeService.getById(item.subjectTypeId).pipe(take(1), map((subjectType) => {
        item.subjectType = new SubjectType(subjectType);
        return item;
      })));
    }

    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        return item;
      }));
    }
    else {
      return of(item);
    }
  }
}
